import { useRef, useState } from "react";
import { Loader2, UploadCloud, X, Plus } from "lucide-react";
import { cn } from "@/lib/utils";
import { useLanguage } from "@/contexts/LanguageContext";
import type { UploadedSource } from "@/pages/BaseballTrend/types";

interface BaseballTrendUploadProps {
  upload: UploadedSource | null;
  isUploading: boolean;
  onAddFile: (file: File) => void;
  onRemove: () => void;
  variant?: "hero" | "compact";
}

/** Single-photo upload control for the Baseball Trend flow.
 *  - `hero` variant: big drop-zone, shown before any photo is uploaded.
 *  - `compact` variant: small thumbnail + remove button, shown afterwards. */
export function BaseballTrendUpload({
  upload,
  isUploading,
  onAddFile,
  onRemove,
  variant = "hero",
}: BaseballTrendUploadProps) {
  const { t } = useLanguage();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  const openPicker = () => inputRef.current?.click();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) onAddFile(file);
    if (inputRef.current) inputRef.current.value = "";
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };
  const handleDragLeave = () => setIsDragging(false);
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = Array.from(e.dataTransfer.files).find((f) =>
      f.type.startsWith("image/"),
    );
    if (file) onAddFile(file);
  };

  // Hero (empty) state — big drop-zone.
  if (!upload && variant === "hero") {
    return (
      <>
        <button
          type="button"
          onClick={openPicker}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          disabled={isUploading}
          className={cn(
            "w-full h-full min-h-[260px] md:min-h-[360px] rounded-2xl border-2 border-dashed bg-card/40 flex flex-col items-center justify-center gap-3 px-6 py-8 transition-all duration-200 text-center",
            isDragging
              ? "border-primary bg-primary/5 scale-[1.01]"
              : "border-border hover:border-primary/50 hover:bg-card",
            isUploading && "opacity-60 cursor-wait",
          )}
        >
          {isUploading ? (
            <Loader2 className="w-10 h-10 text-muted-foreground animate-spin" />
          ) : (
            <>
              <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center">
                <UploadCloud className="w-8 h-8 text-primary" />
              </div>
              <div>
                <p className="text-base md:text-lg font-semibold text-foreground">
                  {t.baseballTrend.uploadDropTitle}
                </p>
                <p className="text-xs md:text-sm text-muted-foreground mt-1">
                  {t.baseballTrend.uploadHint}
                </p>
              </div>
              <p className="text-[11px] text-muted-foreground/80 mt-1">
                {t.baseballTrend.uploadFormats}
              </p>
            </>
          )}
        </button>
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={handleChange}
        />
      </>
    );
  }

  // Compact (post-upload) state — thumbnail + remove button.
  return (
    <div>
      <div className="mb-3">
        <h3 className="text-base md:text-lg font-semibold text-foreground">
          {t.baseballTrend.uploadTitle}
        </h3>
        <p className="text-xs text-muted-foreground mt-0.5">
          {t.baseballTrend.uploadHint}
        </p>
      </div>

      <div className="grid grid-cols-3 gap-3 md:gap-4">
        {upload ? (
          <div className="relative aspect-square rounded-2xl overflow-hidden border border-border bg-card">
            <img
              src={upload.previewUrl}
              alt={t.baseballTrend.uploadTitle}
              className="absolute inset-0 w-full h-full object-cover"
            />
            <button
              type="button"
              onClick={onRemove}
              aria-label={t.baseballTrend.removePhoto}
              className="absolute top-2 end-2 w-7 h-7 bg-background/90 hover:bg-background rounded-full flex items-center justify-center shadow-md"
            >
              <X className="w-4 h-4 text-foreground" />
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={openPicker}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            disabled={isUploading}
            className={cn(
              "aspect-square rounded-2xl border-2 border-dashed bg-card flex flex-col items-center justify-center gap-2 transition-all duration-200",
              isDragging
                ? "border-primary bg-primary/5"
                : "border-border hover:border-primary/40",
              isUploading && "opacity-60",
            )}
          >
            {isUploading ? (
              <Loader2 className="w-6 h-6 text-muted-foreground animate-spin" />
            ) : (
              <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                <Plus className="w-5 h-5 text-primary" />
              </div>
            )}
          </button>
        )}
      </div>

      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={handleChange}
      />
    </div>
  );
}
